import { useEffect, useState } from 'react';
import type { PaymentDetail as PaymentDetailDto } from '@pet/shared';
import { deletePayment, ReceiptApiError } from '../lib/paymentClient';
import { formatCentsAsCurrency } from '../lib/money';
import { formatIsoDate } from '../lib/displayDate';
import styles from './Payments.module.css';

interface DeletePaymentConfirmProps {
  payment: PaymentDetailDto;
  // The host's return button name ("Back to payments" / "Back to expenses"), shown after the delete.
  backLabel: string;
  onKeep: () => void;
  onDeleted: () => void;
  // "Fix this payment" (AD-19): the same delete, then the prefilled wizard instead of "Deleted ✓".
  mode?: 'delete' | 'fix';
  // Fix mode: a note or files the new entry will not carry (release-checkpoint.md F1).
  carriesOver?: boolean;
  onFixed?: (resetIngestionId: string | null) => void;
}

// Fix mode's words (release-checkpoint.md F1, F5).
const FIX_LIVE_SENTENCE =
  'The button below deletes this payment now, then opens Add Spending with its answers filled in. You will choose where the money came from again. A copy is kept for the records.';
const FIX_CARRIES_OVER = 'Its note and attached files stay in the records copy but are not added to the new entry.';

type Step =
  | { name: 'confirm' }
  | { name: 'deleting' }
  | { name: 'deleted' }
  | { name: 'refused'; message: string };

const REASON_MAX_LENGTH = 200;

// HTTP-status -> message mapping, local to the screen as in PaymentDetail. A 401 shows a retryable
// message and never logs the user out (AD-06).
function mapDeleteError(err: unknown): string {
  if (err instanceof ReceiptApiError) {
    switch (err.status) {
      case 409:
        if (err.message === 'Settled') {
          return "This payment's owner payback is marked paid back, so it can't be deleted here.";
        }
        if (err.message === 'Imported') return "This payment came from a bank import, so it can't be deleted here.";
        if (err.message === 'Linked') {
          return "This payment is linked to a lease or a schedule, so it can't be deleted here.";
        }
        break;
      case 400:
        return 'That reason is too long. Please shorten it to one line.';
      case 404:
        return "That payment couldn't be found. It may already be deleted.";
      case 401:
        return "Your session couldn't be confirmed. Please try again.";
    }
  }
  return "We couldn't delete this payment. Please try again — nothing was deleted.";
}

// The full-screen confirm for "Delete this payment" (AD-19). It has no top bar: "Keep it" is the
// back-out and comes first, and the screen says plainly that its delete button acts now.
export default function DeletePaymentConfirm({
  payment,
  backLabel,
  onKeep,
  onDeleted,
  mode = 'delete',
  carriesOver = false,
  onFixed,
}: DeletePaymentConfirmProps) {
  const fixing = mode === 'fix';
  const [step, setStep] = useState<Step>({ name: 'confirm' });
  const [reason, setReason] = useState('');

  // The confirm opens at its top, not where the detail was scrolled to.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  async function confirmDelete() {
    setStep({ name: 'deleting' });
    try {
      const deleted = await deletePayment(payment.id, reason.trim() || undefined);
      if (fixing) {
        onFixed?.(deleted.resetIngestionId);
        return;
      }
      setStep({ name: 'deleted' });
    } catch (err) {
      setStep({ name: 'refused', message: mapDeleteError(err) });
    }
  }

  if (step.name === 'deleted') {
    return (
      <main className={styles.container}>
        <p className={styles.doneText} role="status">
          Deleted ✓
        </p>
        <div className={styles.actionColumn}>
          <button className={styles.primaryButton} onClick={onDeleted}>
            {backLabel}
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className={styles.container}>
      <h1 className={styles.heading}>{fixing ? 'Fix this payment?' : 'Delete this payment?'}</h1>

      <div className={styles.summaryList}>
        <div className={styles.summaryRow}>
          <span className={styles.summaryLabel}>Paid to</span>
          <span className={styles.summaryValue}>{payment.payee}</span>
        </div>
        <div className={styles.summaryRow}>
          <span className={styles.summaryLabel}>Date</span>
          <span className={styles.summaryValue}>{formatIsoDate(payment.date)}</span>
        </div>
        <div className={styles.summaryRow}>
          <span className={styles.summaryLabel}>Expenses in this payment ({payment.lineCount})</span>
        </div>
        {payment.reimbursement && (
          <div className={styles.summaryRow}>
            <span className={styles.summaryLabel}>Owed to</span>
            <span className={styles.summaryValue}>{payment.reimbursement.ownerName}</span>
          </div>
        )}
      </div>
      <div className={styles.summaryTotal}>
        <span>Total</span>
        <span>{formatCentsAsCurrency(payment.totalCents)}</span>
      </div>

      {step.name === 'refused' && (
        <p className={styles.banner} role="alert">
          {step.message}
        </p>
      )}

      <div className={styles.actionColumn}>
        <button className={styles.secondaryButton} onClick={onKeep} disabled={step.name === 'deleting'}>
          Keep it
        </button>

        {step.name !== 'refused' && (
          <>
            <label className={styles.sectionHeading} htmlFor="delete-reason">
              Why? (optional)
            </label>
            <input
              id="delete-reason"
              className={styles.reasonInput}
              type="text"
              maxLength={REASON_MAX_LENGTH}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={step.name === 'deleting'}
            />
            {fixing && carriesOver && <p className={styles.emptyNote}>{FIX_CARRIES_OVER}</p>}
            {fixing ? (
              <p className={styles.emptyNote}>{FIX_LIVE_SENTENCE}</p>
            ) : (
              <p className={styles.emptyNote}>
                The button below deletes this payment, its expenses and any owner payback now. A copy is kept for the
                records.
              </p>
            )}
            <button className={styles.dangerButton} onClick={confirmDelete} disabled={step.name === 'deleting'}>
              {step.name === 'deleting' ? 'Deleting…' : fixing ? 'Delete and fix' : 'Delete this payment'}
            </button>
          </>
        )}
      </div>
    </main>
  );
}
